import React, { useState, useEffect } from 'react';
import { TextField, Button, IconButton, Container, Paper, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { IoAddCircleSharp } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import { GiTennisCourt } from "react-icons/gi";
import axios from 'axios';
import { API_BASE_URL } from '../../App.js';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const Courts = () => {
    const [courts, setCourts] = useState([]);
    const [newCourtName, setNewCourtName] = useState('');
    const [newCourtNumber, setNewCourtNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question',
        confirmText: 'Bestätigen'
    });

    useEffect(() => {
        fetchCourts();
    }, []);

    // Automatische Nummerierung und Benennung, wenn sich die Liste der Courts ändert
    useEffect(() => {
        if (courts.length >= 0) {
            // Finde die kleinste freie Nummer in der Sequenz (Lückenschluss)
            let nextNum = 1;
            const existingNumbers = courts.map(c => c.courtNumber).sort((a, b) => a - b);
            
            for (const num of existingNumbers) {
                if (num === nextNum) {
                    nextNum++;
                } else if (num > nextNum) {
                    break;
                }
            }

            setNewCourtNumber(String(nextNum));
            setNewCourtName(`Platz ${nextNum}`);
        }
    }, [courts]);

    const fetchCourts = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/courts`);
            setCourts(res.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Courts");
        }
    };

    const handleAddCourt = (e) => {
        e.preventDefault();
        if (!newCourtName || !newCourtNumber) return toast.warn("Bitte Name und Nummer angeben");
        
        setConfirmConfig({
            open: true,
            title: 'Platz hinzufügen?',
            message: `Möchtest du einen neuen Platz mit der Bezeichnung "${newCourtName}" wirklich hinzufügen?`,
            type: 'question',
            confirmText: 'Hinzufügen',
            onConfirm: async () => {
                setLoading(true);
                try {
                    await axios.post(`${API_BASE_URL}/api/courts`, {
                        name: newCourtName,
                        courtNumber: parseInt(newCourtNumber)
                    });
                    toast.success("Platz erfolgreich erstellt");
                    fetchCourts();
                } catch (error) {
                    toast.error(error.response?.data?.message || "Fehler beim Erstellen");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleDeleteCourt = async (id) => {
        try {
            const resCount = await axios.get(`${API_BASE_URL}/api/courts/${id}/future-bookings/count`);
            const count = resCount.data.count;
            
            setConfirmConfig({
                open: true,
                title: 'Platz löschen?',
                message: count > 0 
                    ? `ACHTUNG: Auf diesem Platz gibt es noch ${count} zukünftige Buchungen. Wenn du den Platz löschst, werden auch alle diese Buchungen unwiderruflich gelöscht!`
                    : "Möchtest du diesen Platz wirklich löschen?",
                type: 'delete',
                confirmText: 'Löschen',
                onConfirm: async () => {
                    try {
                        await axios.delete(`${API_BASE_URL}/api/courts/${id}`);
                        toast.success("Platz gelöscht");
                        fetchCourts();
                    } catch (error) {
                        toast.error("Fehler beim Löschen");
                    }
                }
            });
        } catch (error) {
            toast.error("Fehler beim Abrufen der Buchungsdaten");
        }
    };

    const handleToggleStatus = (id, currentStatus) => {
        const action = currentStatus ? 'deaktivieren' : 'aktivieren';
        setConfirmConfig({
            open: true,
            title: `Platz ${action}?`,
            message: `Möchtest du diesen Platz wirklich ${action}? ${currentStatus ? 'Der Platz kann dann nicht mehr gebucht werden.' : 'Der Platz steht dann wieder für Buchungen zur Verfügung.'}`,
            type: currentStatus ? 'warning' : 'info',
            confirmText: currentStatus ? 'Deaktivieren' : 'Aktivieren',
            onConfirm: async () => {
                try {
                    await axios.patch(`${API_BASE_URL}/api/courts/${id}/status`, { isActive: !currentStatus });
                    toast.success(currentStatus ? "Platz deaktiviert" : "Platz aktiviert");
                    fetchCourts();
                } catch (error) {
                    toast.error("Fehler beim Ändern des Status");
                }
            }
        });
    };

    return (
        <Container maxWidth="lg" disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
                        Platzverwaltung
                    </Typography>
                </Box>

                {/* Formular zum Hinzufügen */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <TextField
                        label="Platz Name"
                        variant="outlined"
                        size="small"
                        disabled
                        value={newCourtName}
                        sx={{ flexGrow: { xs: 1, sm: 0 } }}
                    />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<IoAddCircleSharp />}
                        onClick={handleAddCourt}
                        disabled={loading}
                        sx={{ minWidth: { xs: 'auto', sm: '120px' } }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Hinzufügen
                        </Box>
                    </Button>
                </Box>

                {/* Liste der Courts */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nr.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Name</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Aktion</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {courts.map((court) => (
                                <TableRow key={court._id} hover>
                                    <TableCell align="center" className="fs-7">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <GiTennisCourt className={court.isActive ? 'text-success' : 'text-muted'} /> 
                                            {court.courtNumber}
                                        </Box>
                                    </TableCell>
                                    <TableCell className="fs-7" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{court.name}</TableCell>
                                    <TableCell align="center" className="fs-7">
                                        <Button 
                                            size="small" 
                                            variant={court.isActive ? "outlined" : "contained"}
                                            color={court.isActive ? "success" : "warning"}
                                            onClick={() => handleToggleStatus(court._id, court.isActive)}
                                        >
                                            {court.isActive ? "Aktiv" : "Inaktiv"}
                                        </Button>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton color="error" onClick={() => handleDeleteCourt(court._id)}>
                                            <MdDelete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {courts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }} className="fs-7">
                                        Keine Plätze vorhanden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Bestätigungs-Dialog */}
            <ConfirmModal
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
            />
        </Container>
    );
};

export default Courts;
